import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { SearchBar } from "../components/SearchBar";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Shield, Zap, Globe, Sparkles } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";

const KONAMI = ["ArrowUp","ArrowUp","ArrowDown","ArrowDown","ArrowLeft","ArrowRight","ArrowLeft","ArrowRight","b","a"];

export default function HomePage() {
  const navigate = useNavigate();
  usePageTitle(null, "OmniSearch est un moteur de recherche moderne et privé : résultats web, images, vidéos et news, résumés IA, OmniMail et OmniTube intégrés.");

  useEffect(() => {
    let buf = [];
    const onKey = (e) => {
      buf.push(e.key.toLowerCase().length === 1 ? e.key.toLowerCase() : e.key);
      buf = buf.slice(-KONAMI.length);
      if (buf.length === KONAMI.length && buf.every((k, i) => k === KONAMI[i].toLowerCase())) {
        navigate("/h4ck");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="px-4 sm:px-6 pt-16 sm:pt-24 pb-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <Logo size="xl" linkTo={null} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black mb-4">
              Cherchez le web.<br />
              <span className="text-gray-400">Pas vous.</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-10 max-w-xl mx-auto">
              OmniSearch combine résultats du monde entier, résumé IA et zéro tracking.
              Appuyez sur <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 font-mono text-xs">/</kbd> pour chercher.
            </p>
            <div className="max-w-2xl mx-auto">
              <SearchBar autoFocus size="lg" />
            </div>
            <div className="mt-6 flex flex-wrap gap-2 justify-center text-xs text-gray-500">
              {["IA générative","Climat 2026","React 19 features","Tokyo travel"].map((s) => (
                <button
                  key={s}
                  data-testid={`quick-search-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => navigate(`/search?q=${encodeURIComponent(s)}&type=web`)}
                  className="px-3 py-1.5 rounded-full border border-[var(--omni-border)] hover:border-black transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="px-4 sm:px-6 py-20 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Shield, t: "Privé par défaut", d: "Aucun traceur, aucun profil publicitaire, vos requêtes restent à vous." },
              { i: Sparkles, t: "Résumé IA", d: "Claude Sonnet 4.5 synthétise les meilleurs résultats en deux phrases." },
              { i: Globe, t: "Tout le web", d: "Web, images, vidéos, news — accès rapide à n'importe quel site du monde." },
              { i: Zap, t: "Ultra rapide", d: "Une recette sans bloat. Le clavier est votre meilleur ami." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="p-6 rounded-lg border border-[var(--omni-border)] bg-white hover:shadow-sm transition">
                <Icon className="text-[var(--omni-accent)] mb-3" size={24} />
                <h3 className="font-bold text-lg mb-1">{t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
